import React, { useState } from 'react';
import { Clock, Plus, Trash2, Save, X } from 'lucide-react';

const RemindersTab = ({ agent, formData, setFormData, onSubmit }) => {
    const remindersEnabled = formData.reminders_enabled ?? false;
    const remindersTimerSeconds = formData.reminders_timer_seconds ?? 30;
    const remindersWords = formData.reminders_words ? JSON.parse(formData.reminders_words) : [];

    const [newWord, setNewWord] = useState('');
    const [error, setError] = useState(null);

    const handleToggle = (e) => {
        e.preventDefault();
        setFormData({
            ...formData,
            reminders_enabled: !remindersEnabled
        });
        setError(null);
    };

    const handleTimerChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        const clampedValue = Math.max(0, Math.min(100, value));
        setFormData({
            ...formData,
            reminders_timer_seconds: clampedValue
        });
    };

    const handleAddWord = () => {
        if (!newWord.trim()) {
            setError('Please enter a reminder phrase');
            return;
        }

        const updatedWords = [...remindersWords, newWord.trim()];
        setFormData({
            ...formData,
            reminders_words: JSON.stringify(updatedWords)
        });
        setNewWord('');
        setError(null);
    };

    const handleRemoveWord = (index) => {
        const updatedWords = remindersWords.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            reminders_words: updatedWords.length > 0 ? JSON.stringify(updatedWords) : null
        });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddWord();
        }
    };

    return (
        <div className="flex-1 overflow-y-auto min-h-0 p-6"
            style={{ 
                scrollBehavior: 'smooth',
                maxHeight: 'calc(100vh - 280px)'
            }}
        >
            <div className="max-w-2xl mx-auto space-y-6">
                {/* Header */}
                <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
                            <Clock className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <div>
                            <h3 className="text-xl font-semibold text-foreground">Reminders</h3>
                            <p className="text-sm text-muted-foreground">Configure reminders when user is silent</p>
                        </div>
                    </div>

                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                        <div>
                            <label className="text-sm font-medium text-foreground">Enable Reminders</label>
                            <p className="text-xs text-muted-foreground mt-1">
                                When enabled, the agent will speak reminder phrases if the user is silent
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                remindersEnabled ? 'bg-primary' : 'bg-muted'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    remindersEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                    </div>

                    {/* Timer Control */}
                    {remindersEnabled && (
                        <div className="mt-4 p-4 bg-muted/20 rounded-lg border border-border/50">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-foreground">Silence Timer (seconds)</label>
                                <span className="text-xs text-muted-foreground">{remindersTimerSeconds}s</span>
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={remindersTimerSeconds}
                                onChange={handleTimerChange}
                                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                <span>0s</span>
                                <span>50s</span>
                                <span>100s</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Agent will speak a random reminder phrase after {remindersTimerSeconds} seconds of silence
                            </p>
                        </div>
                    )}
                </div>

                {/* Reminder Words Management */}
                {remindersEnabled && (
                    <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
                        <h4 className="text-lg font-semibold text-foreground mb-4">Reminder Phrases</h4>
                        
                        {/* Add New Word */}
                        <div className="mb-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newWord}
                                    onChange={(e) => setNewWord(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Enter reminder phrase (e.g., 'Are you still there?')"
                                    className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                                <button
                                    type="button"
                                    onClick={handleAddWord}
                                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add
                                </button>
                            </div>
                        </div>

                        {/* Words List */}
                        {remindersWords.length > 0 ? (
                            <div className="space-y-2">
                                {remindersWords.map((word, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/50"
                                    >
                                        <span className="text-sm text-foreground flex-1">{word}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveWord(index)}
                                            className="p-1.5 hover:bg-destructive/10 rounded-lg transition-colors"
                                            title="Remove phrase"
                                        >
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-muted/20 rounded-lg border border-border/50 p-8 text-center">
                                <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                                <p className="text-sm text-muted-foreground">
                                    No reminder phrases added yet. Add phrases that the agent will speak randomly when the user is silent.
                                </p>
                            </div>
                        )}

                        {error && (
                            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                                <p className="text-sm text-destructive">{error}</p>
                            </div>
                        )}
                    </div>
                )}

                {!remindersEnabled && (
                    <div className="bg-muted/20 rounded-lg border border-border/50 p-8 text-center">
                        <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-sm text-muted-foreground">
                            Enable reminders to configure reminder phrases for silent users
                        </p>
                    </div>
                )}

                {/* Save Button */}
                {agent?.id && (
                    <div className="flex justify-end pt-4 border-t border-border/50">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onSubmit) {
                                    // Create a synthetic submit event
                                    const syntheticEvent = {
                                        preventDefault: () => {},
                                        target: e.target.closest('form') || e.target,
                                        currentTarget: e.target.closest('form') || e.target
                                    };
                                    onSubmit(syntheticEvent);
                                }
                            }}
                            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-primary/30 font-medium"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RemindersTab;

